import React, { useState, useEffect, useRef } from 'react';
import { RotateCw, Crop, PenTool, Check, X, Sliders, Undo } from 'lucide-react';

const ImageEditor = ({ imageSrc, onSave, onCancel, darkMode }) => {
  const canvasRef = useRef(null);
  const [image, setImage] = useState(null);
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270
  
  // Tool Modes: 'none', 'crop', 'draw', 'filter'
  const [activeMode, setActiveMode] = useState('none');
  
  // Drawing States
  const [isDrawing, setIsDrawing] = useState(false);
  const [penColor, setPenColor] = useState('#ff0000');
  const [penWidth, setPenWidth] = useState(5);
  
  // Filter States
  const [brightness, setBrightness] = useState(100); // 100%
  const [contrast, setContrast] = useState(100); // 100%

  // Crop Box States (normalized percent 0-100 for responsiveness)
  const [cropBox, setCropBox] = useState({ x: 10, y: 10, w: 80, h: 80 });
  const [isResizingCrop, setIsResizingCrop] = useState(null); // 'tl', 'tr', 'bl', 'br', 'move'
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [cropBoxStart, setCropBoxStart] = useState({ x: 0, y: 0, w: 0, h: 0 });

  const containerRef = useRef(null);
  const [history, setHistory] = useState([]);

  // Load Image
  useEffect(() => {
    if (!imageSrc) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImage(img);
      setHistory([]);
      setRotation(0);
      setBrightness(100);
      setContrast(100);
    };
    img.src = imageSrc;
  }, [imageSrc]);

  // Redraw canvas when image, rotation, drawing, or filters change
  useEffect(() => {
    if (!image || !canvasRef.current) return;
    drawCanvas();
  }, [image, rotation, brightness, contrast]);

  const saveToHistory = (canvas) => {
    setHistory(prev => [...prev, canvas.toDataURL('image/jpeg', 0.95)]);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const previousState = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImage(img);
    };
    img.src = previousState;
  };

  const drawCanvas = (applyFullQuality = false) => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;
    const ctx = canvas.getContext('2d');

    // Calculate rotated dimensions
    const isHorizontal = rotation === 90 || rotation === 270;
    const originalWidth = image.naturalWidth || image.width;
    const originalHeight = image.naturalHeight || image.height;
    
    let displayWidth = originalWidth;
    let displayHeight = originalHeight;

    // Constrain display size for UI (if not exporting full quality)
    if (!applyFullQuality) {
      const maxDim = Math.min(window.innerWidth * 0.85, window.innerHeight * 0.5);
      const scale = Math.min(maxDim / originalWidth, maxDim / originalHeight);
      displayWidth = Math.round(originalWidth * scale);
      displayHeight = Math.round(originalHeight * scale);
    }

    const w = isHorizontal ? displayHeight : displayWidth;
    const h = isHorizontal ? displayWidth : displayHeight;

    canvas.width = w;
    canvas.height = h;

    // Apply rotation transformations
    ctx.translate(w / 2, h / 2);
    ctx.rotate((rotation * Math.PI) / 180);

    // Apply Filters (Brightness & Contrast)
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;

    // Draw Image centered
    ctx.drawImage(
      image,
      -displayWidth / 2,
      -displayHeight / 2,
      displayWidth,
      displayHeight
    );

    // Reset filter
    ctx.filter = 'none';
  };

  // Drawing Event Handlers
  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const handleStartDraw = (e) => {
    if (activeMode !== 'draw') return;
    saveToHistory(canvasRef.current);
    setIsDrawing(true);
    const pos = getMousePos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const handleDrawing = (e) => {
    if (activeMode !== 'draw' || !isDrawing) return;
    e.preventDefault();
    const pos = getMousePos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = penColor;
    ctx.lineWidth = penWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const handleEndDraw = () => {
    if (activeMode !== 'draw') return;
    setIsDrawing(false);
  };

  // Rotation clockwise
  const rotateClockwise = () => {
    saveToHistory(canvasRef.current);
    setRotation(prev => (prev + 90) % 360);
  };

  // Crop execution
  const executeCrop = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    saveToHistory(canvas);

    // Get cropping coordinates in canvas pixels
    const cropX = (cropBox.x / 100) * canvas.width;
    const cropY = (cropBox.y / 100) * canvas.height;
    const cropW = (cropBox.w / 100) * canvas.width;
    const cropH = (cropBox.h / 100) * canvas.height;

    // Create helper canvas
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = cropW;
    tempCanvas.height = cropH;
    const tempCtx = tempCanvas.getContext('2d');

    // Draw cropped portion
    tempCtx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    // Replace image state with cropped image DataURL
    const croppedDataUrl = tempCanvas.toDataURL('image/jpeg', 0.95);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImage(img);
      setRotation(0);
      setBrightness(100);
      setContrast(100);
      setActiveMode('none');
      // Reset cropbox to 10% padding
      setCropBox({ x: 10, y: 10, w: 80, h: 80 });
    };
    img.src = croppedDataUrl;
  };

  // Crop Box interaction
  const handleCropTouchStart = (handle, e) => {
    e.stopPropagation();
    setIsResizingCrop(handle);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStart({ x: clientX, y: clientY });
    setCropBoxStart({ ...cropBox });
  };

  const handleCropTouchMove = (e) => {
    if (!isResizingCrop) return;
    e.preventDefault();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const deltaX = ((clientX - dragStart.x) / window.innerWidth) * 100;
    const deltaY = ((clientY - dragStart.y) / (window.innerHeight * 0.5)) * 100;

    setCropBox(prev => {
      let { x, y, w, h } = cropBoxStart;
      
      if (isResizingCrop === 'move') {
        x = Math.max(0, Math.min(100 - w, x + deltaX));
        y = Math.max(0, Math.min(100 - h, y + deltaY));
      } else {
        if (isResizingCrop.includes('t')) {
          const newY = Math.max(0, Math.min(y + h - 10, y + deltaY));
          h = h + (y - newY);
          y = newY;
        }
        if (isResizingCrop.includes('b')) {
          h = Math.max(10, Math.min(100 - y, h + deltaY));
        }
        if (isResizingCrop.includes('l')) {
          const newX = Math.max(0, Math.min(x + w - 10, x + deltaX));
          w = w + (x - newX);
          x = newX;
        }
        if (isResizingCrop.includes('r')) {
          w = Math.max(10, Math.min(100 - x, w + deltaX));
        }
      }
      return { x, y, w, h };
    });
  };

  const handleCropTouchEnd = () => {
    setIsResizingCrop(null);
  };

  useEffect(() => {
    if (isResizingCrop) {
      window.addEventListener('mousemove', handleCropTouchMove);
      window.addEventListener('mouseup', handleCropTouchEnd);
      window.addEventListener('touchmove', handleCropTouchMove, { passive: false });
      window.addEventListener('touchend', handleCropTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleCropTouchMove);
      window.removeEventListener('mouseup', handleCropTouchEnd);
      window.removeEventListener('touchmove', handleCropTouchMove);
      window.removeEventListener('touchend', handleCropTouchEnd);
    };
  }, [isResizingCrop, dragStart, cropBoxStart]);

  // Apply All Changes and Export
  const handleSave = () => {
    // Generate final base64 string
    const canvas = canvasRef.current;
    if (!canvas) return;
    const finalDataUrl = canvas.toDataURL('image/jpeg', 0.95);
    onSave(finalDataUrl);
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: darkMode ? '#121212' : '#ffffff',
      color: darkMode ? '#ffffff' : '#121212',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      borderBottom: darkMode ? '1px solid #222' : '1px solid #eee'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold'
    },
    btn: {
      background: 'none',
      border: 'none',
      color: 'inherit',
      cursor: 'pointer',
      padding: '8px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: darkMode ? '#222' : '#f0f0f0'
    },
    saveBtn: {
      backgroundColor: '#28a745',
      color: '#fff',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '20px',
      fontWeight: 'bold',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    workArea: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      padding: '20px',
      backgroundColor: darkMode ? '#0a0a0a' : '#fafafa'
    },
    canvasWrapper: {
      position: 'relative',
      display: 'inline-block'
    },
    canvas: {
      display: 'block',
      maxWidth: '100%',
      maxHeight: '100%',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      borderRadius: '4px'
    },
    cropOverlay: {
      position: 'absolute',
      border: '2px solid #0088FE',
      boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
      boxSizing: 'border-box',
      cursor: 'move'
    },
    cropHandle: {
      position: 'absolute',
      width: '14px',
      height: '14px',
      backgroundColor: '#0088FE',
      borderRadius: '50%',
      border: '2px solid #fff'
    },
    toolbar: {
      padding: '16px',
      borderTop: darkMode ? '1px solid #222' : '1px solid #eee',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    toolRow: {
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center'
    },
    toolBtn: {
      background: 'none',
      border: 'none',
      color: 'inherit',
      cursor: 'pointer',
      padding: '10px',
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      fontSize: '11px',
      transition: 'all 0.2s'
    },
    sliderGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      padding: '0 16px'
    },
    sliderRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '12px'
    },
    slider: {
      flex: 1,
      height: '4px',
      borderRadius: '2px',
      outline: 'none'
    },
    colorPickerRow: {
      display: 'flex',
      justifyContent: 'center',
      gap: '15px',
      marginBottom: '10px'
    },
    colorDot: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      cursor: 'pointer',
      border: '2px solid transparent',
      boxSizing: 'border-box'
    }
  };

  return (
    <div style={styles.overlay} ref={containerRef}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={onCancel} style={styles.btn} title="취소"><X size={20} /></button>
        <span style={styles.title}>사진 고급 편집</span>
        <div style={{ display: 'flex', gap: '10px' }}>
          {history.length > 0 && (
            <button onClick={handleUndo} style={styles.btn} title="실행 취소"><Undo size={20} /></button>
          )}
          <button onClick={handleSave} style={styles.saveBtn}><Check size={20} /> 적용</button>
        </div>
      </div>

      {/* Canvas Work Area */}
      <div style={styles.workArea}>
        <div style={styles.canvasWrapper}>
          <canvas 
            ref={canvasRef} 
            style={{
              ...styles.canvas,
              cursor: activeMode === 'draw' ? 'crosshair' : 'default'
            }}
            onMouseDown={handleStartDraw}
            onMouseMove={handleDrawing}
            onMouseUp={handleEndDraw}
            onMouseLeave={handleEndDraw}
            onTouchStart={handleStartDraw}
            onTouchMove={handleDrawing}
            onTouchEnd={handleEndDraw}
          />
          
          {/* Draggable Crop Box Overlay */}
          {activeMode === 'crop' && (
            <div 
              style={{
                ...styles.cropOverlay,
                left: `${cropBox.x}%`,
                top: `${cropBox.y}%`,
                width: `${cropBox.w}%`,
                height: `${cropBox.h}%`
              }}
              onMouseDown={(e) => handleCropTouchStart('move', e)}
              onTouchStart={(e) => handleCropTouchStart('move', e)}
            >
              {/* Crop Box Handles */}
              <div 
                style={{ ...styles.cropHandle, left: '-7px', top: '-7px', cursor: 'nwse-resize' }} 
                onMouseDown={(e) => handleCropTouchStart('tl', e)}
                onTouchStart={(e) => handleCropTouchStart('tl', e)}
              />
              <div 
                style={{ ...styles.cropHandle, right: '-7px', top: '-7px', cursor: 'nesw-resize' }} 
                onMouseDown={(e) => handleCropTouchStart('tr', e)}
                onTouchStart={(e) => handleCropTouchStart('tr', e)}
              />
              <div 
                style={{ ...styles.cropHandle, left: '-7px', bottom: '-7px', cursor: 'nesw-resize' }} 
                onMouseDown={(e) => handleCropTouchStart('bl', e)}
                onTouchStart={(e) => handleCropTouchStart('bl', e)}
              />
              <div 
                style={{ ...styles.cropHandle, right: '-7px', bottom: '-7px', cursor: 'nwse-resize' }} 
                onMouseDown={(e) => handleCropTouchStart('br', e)}
                onTouchStart={(e) => handleCropTouchStart('br', e)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Editor Controls / Toolbar */}
      <div style={styles.toolbar}>
        
        {/* Draw Mode Tool Options */}
        {activeMode === 'draw' && (
          <div>
            <div style={styles.colorPickerRow}>
              {['#ff0000', '#ffc107', '#28a745', '#007bff', '#ffffff', '#000000'].map(c => (
                <div 
                  key={c}
                  onClick={() => setPenColor(c)}
                  style={{
                    ...styles.colorDot,
                    backgroundColor: c,
                    borderColor: penColor === c ? (darkMode ? '#fff' : '#000') : 'transparent',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                  }}
                />
              ))}
            </div>
            <div style={styles.sliderRow}>
              <span style={{ minWidth: '40px' }}>두께</span>
              <input 
                type="range" 
                min="2" 
                max="20" 
                value={penWidth}
                onChange={(e) => setPenWidth(Number(e.target.value))}
                style={styles.slider}
              />
              <span style={{ minWidth: '20px', textAlign: 'right' }}>{penWidth}px</span>
            </div>
          </div>
        )}

        {/* Filter Mode Adjusters */}
        {activeMode === 'filter' && (
          <div style={styles.sliderGroup}>
            <div style={styles.sliderRow}>
              <span style={{ minWidth: '40px' }}>밝기</span>
              <input 
                type="range" 
                min="50" 
                max="150" 
                value={brightness}
                onChange={(e) => setBrightness(Number(e.target.value))}
                style={styles.slider}
              />
              <span style={{ minWidth: '35px', textAlign: 'right' }}>{brightness}%</span>
            </div>
            <div style={styles.sliderRow}>
              <span style={{ minWidth: '40px' }}>대비</span>
              <input 
                type="range" 
                min="50" 
                max="150" 
                value={contrast}
                onChange={(e) => setContrast(Number(e.target.value))}
                style={styles.slider}
              />
              <span style={{ minWidth: '35px', textAlign: 'right' }}>{contrast}%</span>
            </div>
          </div>
        )}

        {/* Crop Execute Option */}
        {activeMode === 'crop' && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button 
              onClick={executeCrop} 
              style={{
                backgroundColor: '#0088FE',
                color: '#fff',
                border: 'none',
                padding: '8px 20px',
                borderRadius: '20px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              자르기 적용
            </button>
          </div>
        )}

        {/* Primary Modes Selection Row */}
        <div style={styles.toolRow}>
          <button 
            onClick={() => setActiveMode(activeMode === 'crop' ? 'none' : 'crop')}
            style={{
              ...styles.toolBtn,
              backgroundColor: activeMode === 'crop' ? 'rgba(0,136,254,0.1)' : 'transparent',
              color: activeMode === 'crop' ? '#0088FE' : 'inherit'
            }}
          >
            <Crop size={22} />
            <span>자르기</span>
          </button>

          <button 
            onClick={rotateClockwise}
            style={styles.toolBtn}
          >
            <RotateCw size={22} />
            <span>회전</span>
          </button>

          <button 
            onClick={() => setActiveMode(activeMode === 'draw' ? 'none' : 'draw')}
            style={{
              ...styles.toolBtn,
              backgroundColor: activeMode === 'draw' ? 'rgba(0,136,254,0.1)' : 'transparent',
              color: activeMode === 'draw' ? '#0088FE' : 'inherit'
            }}
          >
            <PenTool size={22} />
            <span>그리기</span>
          </button>

          <button 
            onClick={() => setActiveMode(activeMode === 'filter' ? 'none' : 'filter')}
            style={{
              ...styles.toolBtn,
              backgroundColor: activeMode === 'filter' ? 'rgba(0,136,254,0.1)' : 'transparent',
              color: activeMode === 'filter' ? '#0088FE' : 'inherit'
            }}
          >
            <Sliders size={22} />
            <span>필터/밝기</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
